import { AsyncPipe, NgIf } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewEncapsulation,
} from '@angular/core';
import {
  Appointment,
  AppointmentsGroupBy,
  Dossier,
  Fitter,
  InterventionParams,
} from '@sc/data-access-models';
import { AppointmentsSubService } from '@sc/feature-appointments-subscription';
import {
  FITTER_NAME_DISPLAY_CONTEXT,
  FitterNameDisplayContext,
  FitterNameDisplayService,
} from '@sc/feature-contextual-display';
import { Observable, combineLatest, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { FitterListPhoneNumbersComponent } from '../fitter-list-phone-numbers';
import { AppointmentEarliestListComponent } from './appointment-earliest-list';
import { AppointmentNearestListComponent } from './appointment-nearest-list';
import { NoAppointmentsListComponent } from './no-appointments-list';

@Component({
  selector: 'sc-appointment-common-list',
  styleUrls: ['./appointment-common-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  providers: [
    FitterNameDisplayService,
    {
      provide: FITTER_NAME_DISPLAY_CONTEXT,
      useValue: FitterNameDisplayContext.SlotListDefault,
    },
  ],
  imports: [
    NgIf,
    NoAppointmentsListComponent,
    AppointmentNearestListComponent,
    AppointmentEarliestListComponent,
    FitterListPhoneNumbersComponent,
    AsyncPipe,
  ],
  template: `
    <div class="main-container">
      <div class="appointment-list">
        <sc-no-appointments-list
          *ngIf="!(canDisplayResultList$ | async) && !noBookDisplay"
          [hasFitters]="
            !!(appointmentSubscription.displayedFitters$ | async)?.length
          "
          [loading]="loading"
        ></sc-no-appointments-list>
        <sc-appointment-nearest-list
          tourAnchor="intervention-best-slots"
          *ngIf="
            (canDisplayResultList$ | async) &&
            groupBy === 'fitters' &&
            !noBookDisplay
          "
          [loading]="loading"
          [fittersById]="getFittersById() | async"
          [appointmentsByFittersByDays]="
            appointmentSubscription.filteredAppointmentsMapByFitterByDay$
              | async
          "
          [displayGarageAppointments]="filters?.fitterIntervention"
          [displayHomeAppointments]="
            filters?.outsideIntervention && canFilterOutsideIntervention
          "
          [displayReplacementVehicle]="displayReplacementVehicle"
          [displayOnlyReplacementVehicle]="filters?.replacementVehicle"
          [hasMoreFittersToShow]="displayBtnShowMoreFitters$ | async"
          [selectedAppointment]="selectedAppointment"
          [selectedDay]="selectedDay"
          [displaySelectFitter]="displaySelectFitter"
          [dossier]="dossier"
          [highlightBestScoreFitter]="highlightBestScoreFitter"
          [bestScoredFittterId]="getBestScoredFitterId() | async"
          [noAppointmentFitters]="
            appointmentSubscription.displayedFittersWithNoAppointment$ | async
          "
          [noSlotFitters]="
            appointmentSubscription.displayedNoAppointmentFitterCards$ | async
          "
          [noDotfittersIDWithOutsideApp]="
            appointmentSubscription.noDotFittersIDWithOutsideApp$ | async
          "
          [weatherEventFitters]="
            appointmentSubscription.weatherEventFitterCards$ | async
          "
          [nbDisplayedFittersWithSlot]="
            appointmentSubscription.nbDisplayedWithSlotFitters$ | async
          "
          [nbDisplayedFittersNoSlot]="
            appointmentSubscription.nbDisplayedNoSlotFitters$ | async
          "
          (slotClickEvent)="onSlotClick($event)"
          (requestAppointment)="onRequestAppointment($event)"
          (showFitterClickEvent)="onShowDetailsClick($event)"
          (fitterHoverEvent)="onFitterHover($event)"
          (showMoreFittersEvent)="onShowMoreFitters()"
        ></sc-appointment-nearest-list>
        <sc-appointment-earliest-list
          tourAnchor="intervention-best-slots"
          *ngIf="
            (canDisplayResultList$ | async) &&
            groupBy === 'days' &&
            !noBookDisplay
          "
          [loading]="loading"
          [fittersById]="getFittersById() | async"
          [appointmentsByDaysByFitters]="
            appointmentSubscription.filteredAppointmentsMapByDayByFitter$
              | async
          "
          [displayGarageAppointments]="filters?.fitterIntervention"
          [displayHomeAppointments]="
            filters?.outsideIntervention && canFilterOutsideIntervention
          "
          [weatherEventFitters]="
            appointmentSubscription.weatherEventFitterCards$ | async
          "
          [displayReplacementVehicle]="displayReplacementVehicle"
          [displayOnlyReplacementVehicle]="filters?.replacementVehicle"
          [hasMoreFittersToShow]="displayBtnShowMoreFitters$ | async"
          [selectedDay]="selectedDay"
          [selectedAppointment]="selectedAppointment"
          [displaySelectFitter]="displaySelectFitter"
          [dossier]="dossier"
          [highlightBestScoreFitter]="highlightBestScoreFitter"
          [bestScoredFittterId]="getBestScoredFitterId() | async"
          [noAppointmentFitters]="
            appointmentSubscription.displayedFittersWithNoAppointment$ | async
          "
          [noDotfittersIDWithOutsideApp]="
            appointmentSubscription.noDotFittersIDWithOutsideApp$ | async
          "
          (slotClickEvent)="onSlotClick($event)"
          (requestAppointment)="onRequestAppointment($event)"
          (showFitterClickEvent)="onShowDetailsClick($event)"
          (fitterHoverEvent)="onFitterHover($event)"
          (showMoreFittersEvent)="onShowMoreFitters()"
        ></sc-appointment-earliest-list>
        <sc-fitter-list-phone-numbers
          *ngIf="noBookDisplay"
          [fitters]="appointmentSubscription.displayedFitters$ | async"
        ></sc-fitter-list-phone-numbers>
      </div>
    </div>
  `,
})
export class AppointmentCommonListComponent implements OnInit {
  @Input() filters: InterventionParams;
  @Input() selectedAppointment: Appointment;
  @Input() displayReplacementVehicle = true;
  @Input() canFilterOutsideIntervention = true;
  @Input() groupBy: AppointmentsGroupBy = 'fitters';
  @Input() noBookDisplay = false;
  @Input() selectedDay?: Date;
  @Input() dossier: Dossier;
  @Input() displaySelectFitter = true;
  @Input() highlightBestScoreFitter = false;
  @Input() appointmentSubscription: AppointmentsSubService;
  @Input() loading: boolean;
  @Input() shouldDelayNoSlotFitterDisplay = false;

  @Output() slotClickEvent = new EventEmitter<Appointment>();
  @Output() requestAppointment = new EventEmitter<string>();
  @Output() showFitterClickEvent = new EventEmitter<{
    brandId: string;
    fitterId: string;
  }>();
  @Output() fitterHoverEvent = new EventEmitter<string>();

  canDisplayResultList$: Observable<boolean>;
  noFiltersActivated$: Observable<boolean>;

  nbDisplayedFittersWithSlot$: Observable<number>;
  displayBtnShowMoreFitters$: Observable<boolean>;

  //Below attributes are to handle the display delay for noSlot fitter cards
  shouldDisplayFitterCards = false;
  fitterCardsDisplayTimeout;

  ngOnInit(): void {
    if (this.shouldDelayNoSlotFitterDisplay) {
      this.fitterCardsDisplayTimeout = setTimeout(() => {
        this.shouldDisplayFitterCards = true;
      }, 20000);
    } else {
      this.shouldDisplayFitterCards = true;
    }

    this.noFiltersActivated$ = combineLatest([
      this.appointmentSubscription.inputObservables.atHome,
      this.appointmentSubscription.inputObservables.inCenter,
      this.appointmentSubscription.inputObservables.generalists,
      this.appointmentSubscription.inputObservables.replacementVehicle,
    ]).pipe(
      map(
        ([atHome, inCenter, generalists, vehicule]) =>
          (!atHome || vehicule) && !inCenter && !generalists
      )
    );

    this.canDisplayResultList$ = combineLatest([
      this.appointmentSubscription.displayedAppointments$,
      this.appointmentSubscription.displayedFittersWithNoAppointment$,
      this.appointmentSubscription.displayedNoAppointmentFitterCards$,
      this.noFiltersActivated$,
    ]).pipe(
      map(
        ([
          displayedApmtList,
          fittersNoApmtList,
          fittersNoSlotList,
          filtersDeactivated,
        ]) => {
          if (displayedApmtList?.length && !!this.fitterCardsDisplayTimeout) {
            this.shouldDisplayFitterCards = true;
            clearTimeout(this.fitterCardsDisplayTimeout);
          }
          const shouldDisplayFitterCards = this.shouldDisplayFitterCards;
          return {
            displayedApmtList,
            fittersNoApmtList,
            fittersNoSlotList,
            filtersDeactivated,
            shouldDisplayFitterCards,
          };
        }
      ),
      switchMap((result) => {
        return of(
          !result.filtersDeactivated &&
            (!!result.displayedApmtList?.length ||
              (result.fittersNoApmtList.length +
                result.fittersNoSlotList.length >
                0 &&
                result.shouldDisplayFitterCards))
        );
      })
    );

    this.displayBtnShowMoreFitters$ = combineLatest([
      this.appointmentSubscription.dotFitters$,
      this.appointmentSubscription.appointmentsByFitterByDay$,
      this.appointmentSubscription.noDotFittersIDWithOutsideApp$,
      this.appointmentSubscription.noAppointmentFitterCards$,
      this.appointmentSubscription.nbDisplayedNoSlotFitters$,
      this.appointmentSubscription.inputObservables.generalists,
    ]).pipe(
      map(
        ([
          dotFitters,
          apmtByFitterByDay,
          fitterOutsideApmtIds,
          fitterNoSlot,
          nbDisplayedNoSlot,
          displayGeneralists,
        ]) => {
          const idsList = Array.from(apmtByFitterByDay?.keys() || [])
            .filter((id) => !fitterOutsideApmtIds.includes(id))
            .filter((id) =>
              dotFitters
                ?.filter((df) => df.cacheAppointments)
                .map((df) => df.id)
                .includes(id)
            );

          const canShowMoreFittersWithSlot =
            dotFitters
              ?.filter((df) => df.cacheAppointments)
              .filter((df) =>
                Array.from(apmtByFitterByDay?.keys() || []).includes(df.id)
              ).length > idsList.length;
          const canShowMoreFittersNoSlot =
            nbDisplayedNoSlot < fitterNoSlot.length;

          const showBtnMoreFitter = displayGeneralists
            ? canShowMoreFittersNoSlot
            : canShowMoreFittersWithSlot;

          return showBtnMoreFitter;
        }
      )
    );

    this.nbDisplayedFittersWithSlot$ =
      this.appointmentSubscription.nbDisplayedWithSlotFitters$;
  }

  getFittersById() {
    return this.appointmentSubscription.displayedFitters$.pipe(
      map((fittersList) => {
        const fittersMap = new Map<string, Fitter>();

        fittersList.forEach((f) => {
          fittersMap.set(f.id, f);
        });

        return fittersMap;
      })
    );
  }

  getBestScoredFitterId() {
    return this.appointmentSubscription.displayedFitters$.pipe(
      map(
        (fitters) =>
          fitters.find(
            (f) =>
              fitters.findIndex(
                (f2) => f.globalScore < f2.globalScore && f.id !== f2.id
              ) === -1
          )?.id
      )
    );
  }

  onSlotClick($event: Appointment) {
    this.slotClickEvent.emit($event);
  }

  onRequestAppointment(fitterId: string) {
    this.requestAppointment.emit(fitterId);
  }

  onShowDetailsClick($event: { brandId: string; fitterId: string }) {
    this.showFitterClickEvent.emit($event);
  }

  onFitterHover(event) {
    this.fitterHoverEvent.emit(event);
  }

  onShowMoreFitters() {
    this.appointmentSubscription.displayMoreWithSlotFitters();
    this.appointmentSubscription.displayMoreNoSlotFitters();
  }
}
